import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AppIcon } from '../app/AppIcon.js';
import { motion } from 'motion/react';
import { ApiError, auth, type SessionUser } from '../api/client.js';

/**
 * The second half of a sign-in.
 *
 * The password was correct; what is held here is a challenge ticket, not a
 * session. It expires in five minutes and dies after five wrong answers, so the
 * screen shows the clock rather than letting the attempt fail mysteriously at
 * the end — a countdown is the difference between "I was too slow" and "this is
 * broken".
 *
 * Recovery codes are accepted in the same box. Splitting them into a separate
 * screen behind a link is a worse design for the exact moment it exists to
 * serve: someone whose phone is lost or dead, who does not want to hunt for the
 * right tab.
 */

const EASE_SWIM = [0.16, 1, 0.3, 1] as const;

export function MfaChallenge({
  challenge,
  expiresInMs,
  onAuthenticated,
  onCancel,
}: {
  challenge: string;
  expiresInMs: number;
  onAuthenticated: (user: SessionUser) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(() => Math.floor(expiresInMs / 1000));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setRemaining((s) => (s <= 0 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, []);

  const expired = remaining <= 0;

  /*
   * Two shapes are accepted, and the field must not fight either of them: six
   * digits from an authenticator, or a fifteen-character recovery code with
   * hyphens. Stripping non-digits — the obvious thing for a "code" field —
   * would silently make every recovery code untypeable at the one moment the
   * user has nothing else.
   */
  const looksComplete = /^\d{6}$/.test(code.trim()) || code.replace(/[^0-9A-Za-z]/g, '').length >= 15;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!looksComplete || busy || expired) return;

    setBusy(true);
    setError(null);
    try {
      const result = await auth.completeMfa(challenge, code.trim());
      if (result.recoveryCodesRemaining !== undefined && result.recoveryCodesRemaining <= 2) {
        // Running out silently is how someone with a lost phone discovers there
        // is no way back in. Stored for the shell to surface after sign-in.
        sessionStorage.setItem('aksolot:recovery-low', String(result.recoveryCodesRemaining));
      }
      onAuthenticated(result.user);
    } catch (caught) {
      setCode('');
      inputRef.current?.focus();
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'RATE_LIMITED'
            ? `Çok fazla deneme. ${caught.retryAfter ?? 60} saniye sonra tekrar dene.`
            : caught.message,
        );
      } else {
        setError('Beklenmeyen bir hata oldu.');
      }
    } finally {
      setBusy(false);
    }
  }

  const clock = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <motion.div
      className="auth-card"
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: EASE_SWIM }}
    >
      <button type="button" className="auth-back" onClick={onCancel}>
        <AppIcon name="arrowLeft" size={14} hole="transparent" />
        Girişe dön
      </button>

      <form onSubmit={submit} noValidate>
        <div className="auth-step-head">
          <AppIcon name="phone" size={22} hole="transparent" style={{ color: 'var(--tone-encrypted)' }} />
          <h2>İki adımlı doğrulama</h2>
          <p>
            Doğrulayıcı uygulamandaki altı haneli kodu gir. Telefonun yanında değilse kurtarma
            kodlarından birini kullanabilirsin.
          </p>
        </div>

        <label className="field">
          <span className="field-label">Kod</span>
          <input
            ref={inputRef}
            className="field-input field-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="text"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={expired}
            placeholder="000000"
          />
        </label>

        <div className={expired ? 'auth-countdown is-expired' : 'auth-countdown'}>
          {expired ? 'Süre doldu. Yeniden giriş yapman gerekiyor.' : `Kalan süre ${clock}`}
        </div>

        {error !== null ? (
          <div className="auth-error" role="alert">
            <AppIcon name="warning" size={15} hole="transparent" />
            {error}
          </div>
        ) : null}

        <button type="submit" className="auth-submit" disabled={!looksComplete || busy || expired}>
          {busy ? 'Doğrulanıyor…' : 'Doğrula'}
          <span className="send-well">
            <AppIcon name="arrowRight" size={14} hole="transparent" />
          </span>
        </button>
      </form>
    </motion.div>
  );
}
